/**
 * The Learner View shell — hosts LV surfaces as tabs, the way the Console
 * (tc-lab) hosts work surfaces. A learner arriving on a surface route (the
 * LMS's *Surface.aspx handoff redirects here) gets the shell with that
 * surface open beside Home; the launcher opens further instances.
 *
 * HOME IS TAB ZERO. Ported from the Console's useSurfaces invariants: the
 * Home tab is always open, always the strip's leftmost entry, and never
 * closable — it is the markdown mini-site (src/home/LvHomeCanvas.tsx), not a
 * surface you can dismiss. Boot opens Home first; a surface-route arrival
 * then opens that surface on top (when the deployment offers it), and a bare
 * '/' lands on Home alone. Undraggable is structural today — the LV strip has
 * no drag-to-reorder; if one ever lands, Home must be filtered out of the
 * drag set and re-anchored to the left exactly like the Console's moveTabs.
 *
 * Tabs are component instances, not iframes: surfaces are same-origin lazy
 * route modules, so each tab mounts the surface component and inactive tabs
 * hide with display:none — state survives tab switches, and a surface needs
 * no shell-awareness at all. The shell itself renders only behind the
 * session gate (GatedShell): the learner arrives via learner SSO, and the
 * masthead (LvHeader) carries their identity and the sign-out. Each
 * surface's own legacy `#token=` adoption keeps working untouched beneath it.
 *
 * What this deployment offers is driven by the LV client config (the
 * learnerView key of the client DB's Flags.Json, served at
 * /LearnerView/api/config): the launcher's surfaces (an allowlist when the
 * config names one), per-client label/icon substitutions, the theme colour
 * and font on offer, and whether the night/day toggle exists at all. Audio
 * playback reaches surfaces through LvConfigContext.
 *
 * Space rule, mirrored from the Console's SurfacePanel: once GROUP_THRESHOLD
 * (3) open tabs share a surface, the strip folds them into one pull-down chip
 * — "Mailbox ×3 ▾" — whose menu lists the instances. Rows are numbered when
 * labels are identical, because LV surfaces have no context bridge to report
 * per-instance titles yet (the Console's rows prefer tc-surface:context
 * titles; port that when the LV assistant bridge lands).
 *
 * THE SPLIT (Phase 3, cloned from the Console's App.tsx): on desktop the
 * screen is two columns — the ASSISTANT on the left and the surfaces on the
 * right — divided by a drag/click/keyboard resize handle (chat/useResizablePanel,
 * the console's hook). The assistant column self-hides when the deployment
 * has no chat configured (503 on its probe). A maximize toggle on the strip
 * collapses the assistant to 0px so the surfaces take everything (Esc
 * restores). Below MOBILE_BREAK there is no split: surfaces go full-width and
 * the assistant floats in as a full-screen overlay from the cap button.
 */
import { useCallback, useEffect, useMemo, useRef, useState, Suspense } from 'react'
import {
  Box, CircularProgress, CssBaseline, Fab, IconButton, ListItemIcon, ListItemText,
  Menu, MenuItem, Stack, ThemeProvider, Tooltip, Typography, useMediaQuery,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import FlipIcon from '@mui/icons-material/Flip'
import PushPinIcon from '@mui/icons-material/PushPin'
import LockIcon from '@mui/icons-material/Lock'
import { LV_SURFACES, PAGE_SURFACE_ID_RE, pageSurfaceById, surfaceById, type LvSurfaceDef, type SurfaceTarget } from './registry'
import { fetchSurfaceMenu, type SurfaceMenuItem } from './surfaceMenus'
import { OPEN_SURFACE_EVENT, FLIP_FACE_EVENT, RENAME_TAB_EVENT, type OpenSurfaceTabDetail, type FlipFaceDetail, type RenameTabDetail } from './surfaceRuntime'
import {
  LvConfigContext, audioEnabled, fetchLvConfig, homeTabIcon,
  homeTabLabel, inboxIconEnabled, nightModeAllowed, resolveSurfaces, type LvClientConfig,
} from './lvConfig'
import { buildLvTheme, useBrandAssets } from './brandTheme'
import { apiFetch, clearSession, consumeBootOpenId, type LvSession } from './auth'
import { SurfaceGlyph } from './icons'
import LvHeader from './LvHeader'
import { useBrandMode } from './useBrandMode'
import LvHomeCanvas from '../home/LvHomeCanvas'
import type { HomeNavLink } from '../home/vendor/home/types'
import LvChat from '../chat/Chat'
import ResizeHandle from '../chat/ResizeHandle'
import { HANDLE_WIDTH, PANEL_BAR_HEIGHT, useResizablePanel } from '../chat/useResizablePanel'
import type { ShowInSurfaceArgs, SurfaceOpenTarget } from '../chat/types'

interface ShellTab {
  id: string
  surfaceId: string
  label: string
  /** The open-target's params (e.g. a launcher-flyout pick's menu id),
   *  handed to the surface component as target.params. */
  params?: Record<string, unknown>
  /** Pinned tabs can't be closed and ride into the next session's restored
   *  layout (console parity). */
  pinned?: boolean
  /** Minted only on tabs opened by the boot AUTO machinery (nav.<id>.auto —
   *  the boot auto loop and the menuOp auto effect), never on learner
   *  navigation (launcher, deep links, bootOpen). Rides the persisted layout
   *  so a refreshed auto tab stays guarded by nav.<id>.allowClose === false. */
  autoLoaded?: boolean
}

let tabCounter = 0
function newTabId(surfaceId: string): string {
  tabCounter += 1
  return `${surfaceId}-${tabCounter}-${Math.random().toString(36).slice(2, 7)}`
}

/** Tab labels cap at this many characters; longer names truncate with an
 *  ellipsis and reveal the full title as a hover tooltip. */
const TAB_LABEL_MAX = 50

function truncateTabLabel(label: string): string {
  return label.length > TAB_LABEL_MAX ? `${label.slice(0, TAB_LABEL_MAX).trimEnd()}…` : label
}

/** A tab-strip label: truncated past TAB_LABEL_MAX, full title on rollover.
 *  Short labels render bare — no tooltip parroting what's already visible. */
function TabLabel({ label, bold }: { label: string; bold?: boolean }) {
  const short = truncateTabLabel(label)
  const text = <Typography variant="body2" fontWeight={bold ? 700 : 400}>{short}</Typography>
  return short !== label ? <Tooltip title={label}>{text}</Tooltip> : text
}

/** Once this many open tabs share a surface, the strip collapses them into
 *  one pull-down chip. Below the threshold tabs stay individual chips so
 *  one-click switching still works. Same rule as the Console. */
const GROUP_THRESHOLD = 3

type StripItem =
  | { kind: 'single'; tab: ShellTab }
  | { kind: 'group'; surfaceId: string; tabs: ShellTab[] }

/** Fold the flat tab list into strip items, preserving open order: a group
 *  chip takes the position of its first member. Computed fresh every render,
 *  so a group dissolves back into chips the moment a close drops it below
 *  the threshold. */
function buildStripItems(tabs: ShellTab[]): StripItem[] {
  const bySurface = new Map<string, ShellTab[]>()
  for (const t of tabs) {
    const list = bySurface.get(t.surfaceId)
    if (list) list.push(t)
    else bySurface.set(t.surfaceId, [t])
  }
  const emitted = new Set<string>()
  const items: StripItem[] = []
  for (const t of tabs) {
    const list = bySurface.get(t.surfaceId)!
    if (list.length < GROUP_THRESHOLD) {
      items.push({ kind: 'single', tab: t })
    } else if (!emitted.has(t.surfaceId)) {
      emitted.add(t.surfaceId)
      items.push({ kind: 'group', surfaceId: t.surfaceId, tabs: list })
    }
  }
  return items
}

/** The collapsed chip for 3+ tabs of one surface. The chip only opens the
 *  menu (never hijacks the active tab); each menu row activates its tab and
 *  carries its own controls (flip / pin / close, guarded the same as the
 *  strip). The chip itself is a drag drop-target for its first member. */
function GroupChip({
  surfaceId, tabs, activeId, onSelect, onClose, iconOf, flipOf, onFlip, onPin, locked, allowPinning, allowClose, onDropTab,
}: {
  surfaceId: string
  tabs: ShellTab[]
  activeId: string | null
  onSelect: (id: string) => void
  onClose: (id: string) => void
  iconOf: (surfaceId: string) => string
  flipOf: (t: ShellTab) => { next: { id: string; label: string } } | undefined
  onFlip: (id: string) => void
  onPin: (id: string) => void
  locked: boolean
  /** nav.<id>.allowPinning !== false — when false the pin control is hidden
   *  and a stored pin is void (the effective-pin rule). */
  allowPinning: boolean
  /** nav.<id>.allowClose !== false — when false an AUTO-LOADED tab shows the
   *  lock-style glyph instead of an X. */
  allowClose: boolean
  onDropTab: (dragId: string, beforeId: string) => void
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const activeInside = tabs.some((t) => t.id === activeId)
  const allSameLabel = tabs.every((t) => t.label === tabs[0].label)
  // The chip label FOLLOWS the active tab when it's inside the group
  // (Douglas 2026-10-05: picking a pool from the menu switched the content
  // but the chip stayed locked on the first tab's name).
  const label = tabs.find((t) => t.id === activeId)?.label
    ?? tabs[0]?.label ?? surfaceById(surfaceId)?.label ?? surfaceId
  return (
    <>
      <Stack
        direction="row" alignItems="center" spacing={0.5}
        onClick={(e) => setAnchor(e.currentTarget)}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }}
        onDrop={(e) => {
          const id = e.dataTransfer.getData('text/lv-tab')
          if (id && tabs[0]) { e.preventDefault(); onDropTab(id, tabs[0].id) }
        }}
        sx={{
          cursor: 'pointer', px: 1.25, py: 0.75, borderRadius: 1.5, m: 0.5,
          bgcolor: activeInside ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
          whiteSpace: 'nowrap',
        }}
      >
        <SurfaceGlyph icon={iconOf(surfaceId)} />
        <TabLabel label={label} bold={activeInside} />
        <Typography variant="caption" color="text.secondary">×{tabs.length}</Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
      </Stack>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {tabs.map((t, i) => {
          // Effective pin: a stored pin is void once the surface disallows
          // pinning (the stale-pin trap — pin hidden + pinned guard would
          // leave an unclosable tab).
          const effPinned = t.pinned === true && allowPinning
          const closeGuarded = !allowClose && t.autoLoaded === true
          return (
          <MenuItem
            key={t.id} selected={t.id === activeId}
            onClick={() => { setAnchor(null); onSelect(t.id) }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}><SurfaceGlyph icon={iconOf(t.surfaceId)} size={16} /></ListItemIcon>
            <ListItemText primary={allSameLabel ? `${t.label} ${i + 1}` : t.label} />
            {flipOf(t) && (
              <Tooltip title={`Flip to ${flipOf(t)!.next.label} view`}>
                <IconButton
                  size="small" aria-label={`Flip to ${flipOf(t)!.next.label} view`}
                  onClick={(e) => { e.stopPropagation(); setAnchor(null); onFlip(t.id) }}
                  sx={{ p: 0.25, ml: 1 }}
                >
                  <FlipIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
            {locked ? (
              <LockIcon sx={{ fontSize: 12, color: 'text.disabled', ml: 1 }} />
            ) : (
              <>
                {allowPinning && (
                  <IconButton
                    size="small" aria-label={effPinned ? 'Unpin the tab' : 'Pin the tab'}
                    onClick={(e) => { e.stopPropagation(); onPin(t.id) }}
                    sx={{ p: 0.25, ml: flipOf(t) ? 0 : 1 }}
                  >
                    <PushPinIcon sx={{ fontSize: 13, color: effPinned ? 'primary.main' : 'text.disabled', transform: effPinned ? 'none' : 'rotate(45deg)' }} />
                  </IconButton>
                )}
                {!effPinned && (closeGuarded ? (
                  <Tooltip title="Set to open automatically by your organization — this tab can't be closed">
                    <LockIcon sx={{ fontSize: 12, color: 'text.disabled', ml: 1 }} />
                  </Tooltip>
                ) : (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onClose(t.id) }}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                ))}
              </>
            )}
          </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

/** The split position, persisted across visits (console: prefs.panelWidth). */
const CHAT_WIDTH_KEY = 'lv.chatWidth.v1'
/** The console's column-slide animation; suppressed while dragging. */
const SLIDE = 'width 0.25s cubic-bezier(0.4,0,0.2,1)'

/** Two-sided surfaces: the remembered face per menu item — Douglas 2026-10-04:
 *  "default to participant, or last known". One key, one JSON map (the
 *  console's tc-connections:lastBranch shape); persistence is a convenience,
 *  so every read/write is guarded and any failure means the default face. */
const LAST_FACE_KEY = 'lv.lastFace.v1'
function readLastFace(): Record<string, string> {
  try {
    const raw = localStorage.getItem(LAST_FACE_KEY)
    const m = raw ? (JSON.parse(raw) as unknown) : {}
    return m && typeof m === 'object' && !Array.isArray(m) ? (m as Record<string, string>) : {}
  } catch {
    return {}
  }
}
function writeLastFace(menuId: string, face: string) {
  try {
    const m = readLastFace()
    m[menuId] = face
    localStorage.setItem(LAST_FACE_KEY, JSON.stringify(m))
  } catch { /* storage blocked — convenience only */ }
}

/** Face → tab glyph / accent colour for two-sided surfaces (the "you can
 *  tell there are two" tell across a row of tabs). Faces are surface-defined
 *  ids; the two the shell knows get real glyphs, anything else falls back to
 *  the surface's own icon and the theme's primary accent. */
const FACE_GLYPH: Record<string, string> = { participant: 'Groups', leader: 'SupervisorAccount' }
const FACE_COLOR: Record<string, string> = { participant: 'primary.main', leader: 'accent.main' }

/** The card-flip keyframes — played by a faced tab's content Box whenever its
 *  key changes (open, and every flip). Inline in sx per the repo's animation
 *  convention (see Chat.tsx's pulse); suppressed under reduced-motion. */
const FLIP_SX = {
  animation: 'lvFaceFlip 0.28s cubic-bezier(0.4,0,0.2,1)',
  '@keyframes lvFaceFlip': {
    from: { transform: 'perspective(1400px) rotateY(-86deg)', opacity: 0.35 },
    to: { transform: 'perspective(1400px) rotateY(0deg)', opacity: 1 },
  },
}
/** The tab-switch transition — a gentle rise+fade, deliberately NOT the flip
 *  (Douglas 2026-10-04: the flip belongs to flips only; tab-to-tab wanted
 *  something quieter). */
const FADE_SX = {
  animation: 'lvTabIn 0.16s ease-out',
  '@keyframes lvTabIn': {
    from: { opacity: 0.35, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}

/** The tab layout, persisted per learner (Douglas 2026-10-04: "only enforce
 *  the predefined surfaces on login — a refresh keeps what I had; closed
 *  stays closed"). One key per learner (privacy on shared browsers); every
 *  read/write is guarded — persistence is a convenience, never a gate. */
interface StoredTabs {
  tabs: { surfaceId: string; label: string; params?: Record<string, unknown>; pinned?: boolean; autoLoaded?: boolean }[]
  /** Index of the active tab in the stored array. */
  active: number
}
function tabsKeyFor(sub: string): string {
  return `lv.tabs.v1.${sub}`
}
function readStoredTabs(sub: string): StoredTabs | null {
  try {
    const raw = localStorage.getItem(tabsKeyFor(sub))
    if (!raw) return null
    const v = JSON.parse(raw) as StoredTabs
    return v && Array.isArray(v.tabs) ? v : null
  } catch {
    return null
  }
}

function defaultPanelWidth(): number {
  // Surfaces get the larger share by default — LV is surface-first.
  return Math.round(window.innerWidth * 0.62)
}

function storedPanelWidth(): number {
  try {
    const raw = localStorage.getItem(CHAT_WIDTH_KEY)
    if (raw) {
      const n = Number(raw)
      if (Number.isFinite(n) && n > 0) return n
    }
  } catch { /* storage blocked */ }
  return defaultPanelWidth()
}

/** One surface tab's content: the lazy surface component behind the shared
 *  Suspense fallback. Extracted so the tab-content map can branch between
 *  this and tab zero's LvHomeCanvas without an inline tangle. The tab's
 *  open-target rides down as the surface's `target` prop. */
function SurfacePane({ surfaceId, target }: { surfaceId: string; target?: SurfaceTarget }) {
  const def = surfaceById(surfaceId)
  if (!def) return null
  const { Component } = def
  return (
    <Suspense fallback={
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    }>
      <Component target={target} />
    </Suspense>
  )
}

interface LearnerIdentity {
  userId: string
  name: string
  email: string
  client: string
  branch: string
}

export default function LvShell({ initialSurfaceId, session }: { initialSurfaceId?: string; session: LvSession }) {
  // undefined = still loading; null = unconfigured (everything default)
  const [cfg, setCfg] = useState<LvClientConfig | null | undefined>(undefined)
  const [tabs, setTabs] = useState<ShellTab[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  // The header identity: token claims immediately, /me when it lands.
  const [me, setMe] = useState<LearnerIdentity | null>(null)
  // Light/dark: the ONE appearance mechanism, shared with the masthead's
  // Appearance menu (policy + lv:mode session choice — see useBrandMode).
  const { mode, allowed: appearanceAllowed, choose: chooseAppearanceMode } = useBrandMode(cfg ?? null)
  // The masthead Explore menu's Home section: the vendored HomeSurface
  // reports its effective site menu + current page (its guest-header
  // channels); homeFollowRef hands its own follow() to an Explore pick.
  const [homeNav, setHomeNav] = useState<{ items: HomeNavLink[]; currentFile?: string }>({ items: [] })
  const homeFollowRef = useRef<((n: HomeNavLink) => void) | null>(null)
  const onHomeNavChange = useCallback(
    (items: HomeNavLink[], currentFile: string | undefined) => setHomeNav({ items, currentFile }),
    [],
  )
  const didInit = useRef(false)

  // ── The split ──────────────────────────────────────────────────────────
  // undefined = probe in flight; the column renders only once the chat
  // backend says it is configured (a 503 = hidden, no dead panel).
  const [chatAvailable, setChatAvailable] = useState<boolean | undefined>(undefined)
  // Surfaces-take-everything mode: the assistant column collapses to 0px.
  const [maximized, setMaximized] = useState(false)
  // Mobile (< MOBILE_BREAK): the assistant rides as an overlay, never a column.
  const [mobileChatOpen, setMobileChatOpen] = useState(false)
  const resizer = useResizablePanel(storedPanelWidth(), (w) => {
    try { localStorage.setItem(CHAT_WIDTH_KEY, String(w)) } catch { /* storage blocked */ }
  })

  useEffect(() => {
    let live = true
    void fetchLvConfig().then((c) => { if (live) setCfg(c) })
    return () => { live = false }
  }, [])

  // The assistant probe: GET the chat history once — 200 doubles as warmup,
  // 503 means "not configured on this deployment" and the column never shows.
  // LvChat refetches the same (cheap, local) history on mount.
  useEffect(() => {
    let live = true
    apiFetch('/LearnerView/api/chat')
      .then((r) => { if (live) setChatAvailable(r.ok ? true : r.status !== 503) })
      .catch(() => { if (live) setChatAvailable(false) })
    return () => { live = false }
  }, [])

  // Esc leaves maximized (the console's rule).
  useEffect(() => {
    if (!maximized) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setMaximized(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [maximized])

  // Fresh identity for the header (and the sliding-renewal round trip). A
  // 401 clears the session inside apiFetch and the gate takes over; any other
  // failure just leaves the token claims on display.
  useEffect(() => {
    let live = true
    void apiFetch('/LearnerView/api/auth/me')
      .then(async (r) => { if (live && r.ok) setMe((await r.json()) as LearnerIdentity) })
      .catch(() => undefined)
    return () => { live = false }
  }, [session.token])

  function signOut() {
    // Stateless sessions: the POST is telemetry/symmetry, the local clear is
    // what actually signs out (and routes the app back to the gate).
    void apiFetch('/LearnerView/api/auth/logout', { method: 'POST' }).catch(() => undefined)
    // The next login re-fires the predefined surfaces — the layout is a
    // refresh convenience, not a cross-session memory.
    try { localStorage.removeItem(tabsKeyFor(session.claims.sub)) } catch { /* storage blocked */ }
    clearSession()
  }

  const surfaces = useMemo(() => (cfg === undefined ? [] : resolveSurfaces(LV_SURFACES, cfg)), [cfg])
  // Folder children (config.folders): the config route emits folder-claimed
  // pages GROUPED, not in the surfaces allowlist — but they ARE offered pages
  // (the Explore folder flyout opens them). Synthesize their defs exactly like
  // resolveSurfaces does for loose pages (pageSurfaceById + emitted label/icon)
  // so the open/restore/deep-link gates below recognize them and Home-content
  // surface links to them survive the offered gate.
  const folderChildDefs = useMemo(() => {
    const out: Record<string, LvSurfaceDef> = {}
    for (const f of cfg?.folders ?? []) {
      for (const c of f.children ?? []) {
        if (out[c.id]) continue
        const def = pageSurfaceById(c.id)
        if (!def) continue
        out[c.id] = { ...def, label: c.label ?? def.label, icon: c.icon ?? def.icon }
      }
    }
    return out
  }, [cfg])
  const offeredIds = useMemo(() => [...surfaces.map((s) => s.id), ...Object.keys(folderChildDefs)], [surfaces, folderChildDefs])

  // ── Launcher flyouts (manifest `menuOp`) ─────────────────────────────────
  // One queue-native fetch per menuOp-declaring surface per boot; a failure
  // (worker predates the op, nav gate, timeout) lands as zero items — the
  // launcher row stays expandable (expandability rides the manifest, Isobel
  // 2026-10-09) and shows its empty state.
  const [menus, setMenus] = useState<Record<string, SurfaceMenuItem[]>>({})
  const menuFetched = useRef<Set<string>>(new Set())
  useEffect(() => {
    for (const s of surfaces) {
      if (!s.menuOp || s.href || menuFetched.current.has(s.id)) continue
      menuFetched.current.add(s.id)
      void fetchSurfaceMenu(s.id, s.menuOp)
        .then((items) => setMenus((m) => ({ ...m, [s.id]: items })))
        // A failure lands as ZERO items: the launcher row expands to its
        // empty state, and an auto surface opens its root tab instead of
        // pending forever.
        .catch(() => setMenus((m) => ({ ...m, [s.id]: [] })))
    }
  }, [surfaces])

  // ── Masthead inbox icon + unread badge (classic login_details.xsl parity) ──
  // Shown only when the settings store says so (nav.inbox.iconEnabled — the
  // classic myInboxIcon toggle, hidden by default) AND the inbox surface is
  // offered. Polls the surface's cached /unread every 60s and on window
  // focus; the server single-flights the count at 30s, so a fleet of tabs
  // costs at most one query per learner per half minute.
  const showInboxIcon = cfg !== undefined && inboxIconEnabled(cfg) && offeredIds.includes('inbox')
  const [unread, setUnread] = useState(0)
  useEffect(() => {
    if (!showInboxIcon) return
    let live = true
    const tick = () => {
      void apiFetch('/LearnerView/api/surfaces/inbox/unread')
        .then(async (r) => { if (live && r.ok) setUnread(((await r.json()) as { unread?: number }).unread ?? 0) })
        .catch(() => undefined)
    }
    tick()
    const t = setInterval(tick, 60_000)
    window.addEventListener('focus', tick)
    return () => { live = false; clearInterval(t); window.removeEventListener('focus', tick) }
  }, [showInboxIcon])

  /** Home — tab zero. Always open, always leftmost, never closable: it is the
   *  mini-site (LvHomeCanvas), not a surface. The id lives in a ref so a
   *  double call inside one commit selects the existing tab instead of
   *  minting a duplicate (the Console's openHome invariant, ported). */
  const homeIdRef = useRef<string | null>(null)
  function openHome(): string {
    if (homeIdRef.current) {
      setActiveId(homeIdRef.current)
      return homeIdRef.current
    }
    const id = newTabId('home')
    homeIdRef.current = id
    // Tab zero's label is the deployment's own when nav.home.label is set —
    // Cox calls its Home something of theirs.
    setTabs((prev) => [{ id, surfaceId: 'home', label: homeTabLabel(cfg ?? null) }, ...prev])
    setActiveId(id)
    return id
  }

  // Boot once the config has settled: Home first (it anchors the strip), then
  // each AUTO surface (nav.<id>.auto — Douglas 2026-10-03: pre-loaded tabs save
  // the learner the launcher trip; any number may be auto). A menuOp auto
  // surface waits for its flyout items — one NAMED TAB PER ITEM — in the menus
  // effect below. Then the route's surface on top when this deployment offers
  // it — a surface-route arrival (the LMS handoff) wants that surface active.
  // A bare '/' visit lands on the FIRST auto tab, or Home alone when nothing
  // is auto. An LMS-initiated deep link (?open=<id>, e.g. the first-login
  // credentials redirect) opens last so it lands on top. Never re-fires.
  const pendingAutoMenus = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (cfg === undefined || didInit.current) return
    didInit.current = true
    // A REFRESH restores the learner's own layout — closed tabs stay closed,
    // pins and order survive (Douglas 2026-10-04). The predefined/auto
    // surfaces fire ONLY when no layout exists (a fresh login). Restored ids
    // are re-minted (the session's counter restarted); Home is re-anchored
    // left and is never stored.
    const stored = readStoredTabs(session.claims.sub)
    if (stored && stored.tabs.length > 0) {
      const restored = stored.tabs
        .filter((t) => t && typeof t.surfaceId === 'string' && (surfaces.some((s) => s.id === t.surfaceId) || folderChildDefs[t.surfaceId] !== undefined))
        .map((t) => {
          const def = surfaces.find((s) => s.id === t.surfaceId) ?? folderChildDefs[t.surfaceId]
          return {
            id: newTabId(t.surfaceId),
            surfaceId: t.surfaceId,
            label: typeof t.label === 'string' ? t.label : t.surfaceId,
            params: t.params,
            // Stale-pin trap: a stored pin from before the surface's
            // allowPinning flipped off must NOT resurrect (pin hidden +
            // pinned close-guard would leave an unclosable tab).
            pinned: t.pinned === true && def?.allowPinning !== false,
            // The auto marker rides the stored tab so a refreshed auto tab
            // stays guarded by nav.<id>.allowClose === false.
            autoLoaded: t.autoLoaded === true,
          }
        })
      if (restored.length > 0) {
        openHome()
        setTabs((prev) => [...prev, ...restored])
        setActiveId(restored[Math.min(stored.active, restored.length - 1)]?.id ?? restored[0].id)
        // A deep-link arrival still wins over the restored layout — the LMS
        // handoff (or a shared link) asked for THIS surface: activate the
        // restored tab when one exists, else open it on top. Same contract
        // as the no-layout boot below; without it /inbox and /page/<slug>
        // silently render whatever the last session left open.
        const first = initialSurfaceId
          ? surfaces.find((s) => s.id === initialSurfaceId) ?? folderChildDefs[initialSurfaceId]
          : undefined
        if (first) {
          const existing = restored.find((t) => t.surfaceId === first.id)
          if (existing) setActiveId(existing.id)
          else openSurface(first.id)
        }
        const bootOpen = consumeBootOpenId()
        if (bootOpen && (surfaces.some((s) => s.id === bootOpen) || folderChildDefs[bootOpen])) openSurface(bootOpen)
        return
      }
    }
    openHome()
    let firstAuto: string | null = null
    for (const s of surfaces) {
      if (s.auto !== true) continue
      if (s.menuOp && !s.href) { pendingAutoMenus.current.add(s.id); continue }
      // Boot-auto opens are marked autoLoaded — nav.<id>.allowClose === false
      // guards exactly these tabs, never the learner's own opens.
      const id = openSurface(s.id, undefined, undefined, { auto: true })
      if (id && !firstAuto) firstAuto = id
    }
    const first = initialSurfaceId
      ? surfaces.find((s) => s.id === initialSurfaceId) ?? folderChildDefs[initialSurfaceId]
      : undefined
    if (first) openSurface(first.id)
    const bootOpen = consumeBootOpenId()
    if (bootOpen && (surfaces.some((s) => s.id === bootOpen) || folderChildDefs[bootOpen])) openSurface(bootOpen)
    else if (!first && firstAuto) setActiveId(firstAuto)
  }, [cfg, surfaces, folderChildDefs, initialSurfaceId])

  // Auto + menuOp: the flyout fetch (the launcher effect above) carries the
  // items; open one tab per item, named after it (e.g. each connections pool
  // the learner participates in). Zero items = the surface's own root tab.
  // Each id fires exactly once (the pending set drains).
  useEffect(() => {
    if (pendingAutoMenus.current.size === 0) return
    for (const id of [...pendingAutoMenus.current]) {
      const items = menus[id]
      if (items === undefined) continue // fetch still in flight
      pendingAutoMenus.current.delete(id)
      const def = surfaces.find((s) => s.id === id)
      if (!def) continue
      if (items.length === 0) openSurface(id, undefined, undefined, { auto: true })
      else for (const it of items) openSurface(id, it.label, { params: { menu: it.id } }, { auto: true })
    }
  }, [menus, surfaces])

  const theme = useMemo(() => buildLvTheme(cfg ?? null, mode), [mode, cfg])
  // The first motion query in the shell: the card-flip sits it out.
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  // Animation bookkeeping: the flip fires ONLY on an actual face change (a
  // flip, or a faced tab's first mount), the fade ONLY when a tab becomes
  // active — refs snapshot after every render so the next one can diff
  // (Douglas 2026-10-04: no more flip on plain tab-to-tab).
  const lastFaceRef = useRef<Record<string, string | undefined>>({})
  const lastActiveRef = useRef<string | null>(null)
  useEffect(() => {
    const faces: Record<string, string | undefined> = {}
    for (const t of tabs) faces[t.id] = faceOf(t)
    lastFaceRef.current = faces
    lastActiveRef.current = activeId
  })
  // Brand webfonts + favicon ride the same resolved config (no rows = no-ops).
  useBrandAssets(cfg ?? null)

  const iconOf = (surfaceId: string) =>
    surfaceId === 'home'
      ? homeTabIcon(cfg ?? null)
      : surfaces.find((s) => s.id === surfaceId)?.icon ?? surfaceById(surfaceId)?.icon ?? 'Tab'

  /** Always a fresh instance — opening Mailbox twice gives two independent
   *  Mailbox tabs, the Console's semantics. A surface with a settings-store
   *  href override (nav.<id>.href — the classic purchase-redirect made real)
   *  opens its external target in a NEW BROWSER TAB instead of a surface tab.
   *  `label` overrides the tab label (a launcher-flyout pick names its tab
   *  after the picked item); the target's params ride the tab down to the
   *  surface component — `params.menu` is how a flyout pick scopes its
   *  surface. The chat's deep-link `view` is accepted for protocol parity
   *  but LV surfaces can't consume it yet — that rides the future bridge.
   *  `opts.auto` is reserved for the boot AUTO machinery: it mints the tab's
   *  autoLoaded marker (nav.<id>.allowClose === false guards exactly those
   *  tabs). Learner navigation — launcher, deep links, bootOpen — never
   *  passes it. */
  function openSurface(surfaceId: string, label?: string, target?: SurfaceOpenTarget, opts?: { auto?: boolean }): string | undefined {
    const def = surfaces.find((s) => s.id === surfaceId) ?? folderChildDefs[surfaceId]
    if (!def) return undefined // not offered on this deployment
    if (def.href) {
      window.open(def.href, '_blank', 'noopener,noreferrer')
      return undefined
    }
    const id = newTabId(surfaceId)
    // Two-sided surfaces: a menu-scoped open with no explicit face resolves
    // the remembered-then-default face, eligibility-filtered (an item with no
    // faces array yields only the default — the flip and the worker's
    // server-side gate enforce the same set). An explicit params.face (a deep
    // link) passes through untouched.
    let params = target?.params
    // Custom pages (page-<slug>): the slug rides down as target.params.slug —
    // LvPageCanvas fetches its def with it. Injected here so EVERY open path
    // (launcher, Explore, boot auto, deep links, a flyout pick) carries it.
    const pageMatch = PAGE_SURFACE_ID_RE.exec(surfaceId)
    if (pageMatch && typeof params?.slug !== 'string') params = { ...params, slug: pageMatch[1] }
    // TAB DEDUPE (Douglas 2026-10-11): one tab per THING, not per click.
    // The same content page (page-<slug>) and the same details page
    // (a surface opened with params.id, e.g. activities) may not be open
    // twice at once — a repeat open focuses the existing tab instead of
    // cloning it. Anything else (menu-scoped picks, plain surfaces) keeps
    // the many-tabs-you-like rule.
    const dedupeKey = pageMatch
      ? surfaceId
      : typeof params?.id === 'string' && params.id ? `${surfaceId}:${params.id.toLowerCase()}` : null
    if (dedupeKey) {
      const existing = tabs.find((t) => {
        if (t.surfaceId !== surfaceId) return false
        if (pageMatch) return true
        const pid = t.params?.id
        return typeof pid === 'string' && `${t.surfaceId}:${pid.toLowerCase()}` === dedupeKey
      })
      if (existing) {
        setActiveId(existing.id)
        setMobileChatOpen(false)
        return existing.id
      }
    }
    if (def.faces?.length && typeof params?.menu === 'string') {
      const menuId = params.menu
      const defFace = def.defaultFace ?? def.faces[0].id
      let face = typeof params.face === 'string' ? params.face : undefined
      if (!face) {
        const item = menus[def.id]?.find((i) => i.id === menuId)
        const eligible = item?.faces?.length
          ? def.faces.filter((f) => item.faces!.includes(f.id))
          : def.faces.filter((f) => f.id === defFace)
        const remembered = readLastFace()[menuId]
        face = eligible.some((f) => f.id === remembered) ? remembered : eligible[0]?.id
      }
      if (face) {
        params = { ...params, face }
        writeLastFace(menuId, face)
      }
    }
    setTabs((prev) => [...prev, { id, surfaceId, label: label ?? def.label, params, autoLoaded: opts?.auto === true ? true : undefined }])
    setActiveId(id)
    // A marker click on mobile dismisses the assistant overlay so the new
    // tab is what the learner sees.
    setMobileChatOpen(false)
    return id
  }

  // The surface→shell bridge: a surface's openSurfaceTab() dispatches
  // OPEN_SURFACE_EVENT (it can't hold openSurface — it mounts below the
  // launcher); open the tab exactly like a launcher pick, caller's tab
  // untouched. A ref keeps the listener bound once, never stale.
  const openSurfaceRef = useRef(openSurface)
  openSurfaceRef.current = openSurface
  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<OpenSurfaceTabDetail>).detail
      if (d?.surfaceId) openSurfaceRef.current(d.surfaceId, d.label, d.params ? { params: d.params } : undefined)
    }
    window.addEventListener(OPEN_SURFACE_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_SURFACE_EVENT, onOpen)
  }, [])

  /** [Show me|…] — the point-at-something gesture. Until the LV bridge can
   *  flash an element, it degrades to opening the surface (console parity of
   *  navigation, minus the highlight). */
  function showInSurface(args: ShowInSurfaceArgs) {
    openSurface(args.surfaceId, undefined, { view: args.view, params: args.params })
  }

  /** An Explore pick of a Home page: activate tab zero, then drive Home's
   *  own follow() down the ref — the pick navigates exactly the way a click
   *  on Home's own nav row would. */
  function followHomeNav(n: HomeNavLink) {
    openHome()
    homeFollowRef.current?.(n)
  }

  /** The faces a tab may flip between: the surface's declared faces filtered
   *  by the menu item's per-learner eligibility (carried by the menuOp
   *  items). Conservative by contract — a menu tab whose item has no faces
   *  array (an older data worker) and any non-menu tab get NO flip. The
   *  worker re-gates every op server-side; this is presentation, never
   *  permission. */
  function faceChoicesFor(t: ShellTab): { id: string; label: string }[] {
    const def = surfaces.find((s) => s.id === t.surfaceId) ?? surfaceById(t.surfaceId)
    if (!def?.faces?.length) return []
    const menuId = typeof t.params?.menu === 'string' ? t.params.menu : undefined
    if (!menuId) return []
    const item = menus[def.id]?.find((i) => i.id === menuId)
    if (!item?.faces?.length) return []
    return def.faces.filter((f) => item.faces!.includes(f.id))
  }

  /** The face a tab is showing: the explicit param, else the surface default. */
  function faceOf(t: ShellTab): string | undefined {
    const def = surfaces.find((s) => s.id === t.surfaceId) ?? surfaceById(t.surfaceId)
    if (!def?.faces?.length) return undefined
    return typeof t.params?.face === 'string' ? t.params.face : (def.defaultFace ?? def.faces[0].id)
  }

  /** Strip-level flip state: defined only when there's somewhere to flip TO. */
  function flipStateFor(t: ShellTab): { next: { id: string; label: string } } | undefined {
    const choices = faceChoicesFor(t)
    if (choices.length < 2) return undefined
    const cur = faceOf(t)
    const next = choices.find((f) => f.id !== cur) ?? choices[0]
    return next ? { next } : undefined
  }

  // Persist the layout for the next refresh (Home is never stored — it is
  // re-anchored at restore). The predefined autos fire only when this store
  // is absent (see the boot effect).
  useEffect(() => {
    if (!didInit.current) return
    try {
      const surfaceTabs = tabs.filter((t) => t.surfaceId !== 'home')
      const active = Math.max(0, surfaceTabs.findIndex((t) => t.id === activeId))
      localStorage.setItem(
        tabsKeyFor(session.claims.sub),
        JSON.stringify({
          tabs: surfaceTabs.map(({ surfaceId, label, params, pinned, autoLoaded }) => ({ surfaceId, label, params, pinned, autoLoaded })),
          active,
        } satisfies StoredTabs),
      )
    } catch { /* storage blocked — convenience only */ }
  }, [tabs, activeId])

  /** Flip a two-sided tab to a SPECIFIC face, eligibility-checked. Shared by
   *  the strip's flip button and the in-surface flip bridge (flipFace()). */
  function flipTabFaceTo(tabId: string, face: string) {
    setTabs((prev) =>
      prev.map((t) => {
        if (t.id !== tabId) return t
        if (!faceChoicesFor(t).some((f) => f.id === face)) return t
        if (typeof t.params?.menu === 'string') writeLastFace(t.params.menu, face)
        return { ...t, params: { ...t.params, face } }
      }),
    )
  }

  // The in-surface flip bridge: a surface's flipFace(tabId, face) dispatches
  // FLIP_FACE_EVENT; a ref keeps the listener bound once, never stale.
  const flipRef = useRef(flipTabFaceTo)
  flipRef.current = flipTabFaceTo
  useEffect(() => {
    const onFlip = (e: Event) => {
      const d = (e as CustomEvent<FlipFaceDetail>).detail
      if (d?.tabId && d?.face) flipRef.current(d.tabId, d.face)
    }
    window.addEventListener(FLIP_FACE_EVENT, onFlip)
    return () => window.removeEventListener(FLIP_FACE_EVENT, onFlip)
  }, [])

  // The rename bridge: a surface's renameTab(tabId, label) dispatches
  // RENAME_TAB_EVENT; rewrite that tab's label (forums names its tab after
  // the forum you're in). `label: null` restores the surface's own resolved
  // label — the per-client substitution, not a string the surface guessed.
  useEffect(() => {
    const onRename = (e: Event) => {
      const d = (e as CustomEvent<RenameTabDetail>).detail
      if (d?.tabId && (typeof d.label === 'string' || d.label === null)) {
        setTabs((prev) => prev.map((t) => (t.id === d.tabId
          ? { ...t, label: d.label ?? surfaceById(t.surfaceId)?.label ?? t.label }
          : t)))
      }
    }
    window.addEventListener(RENAME_TAB_EVENT, onRename)
    return () => window.removeEventListener(RENAME_TAB_EVENT, onRename)
  }, [])

  /** Flip a two-sided tab to its other face IN PLACE — openSurface always
   *  mints a fresh tab (the Console semantics), so the flip can't redispatch:
   *  it mutates the tab's params.face. The content Box keys on the face, so
   *  the pane remounts and the card-flip animation plays; the surface boots
   *  fresh into the new face. The choice is remembered for the next open of
   *  the same menu item (lastFace). */
  function flipTabFace(tabId: string) {
    setTabs((prev) =>
      prev.map((t) => {
        if (t.id !== tabId) return t
        const flip = flipStateFor(t)
        if (!flip) return t
        if (typeof t.params?.menu === 'string') writeLastFace(t.params.menu, flip.next.id)
        return { ...t, params: { ...t.params, face: flip.next.id } }
      }),
    )
  }

  function closeTab(id: string) {
    const t = tabs.find((x) => x.id === id)
    // Tab zero never closes — Home is the permanent tab, not a surface you
    // dismiss. The strip never offers its X (below); this guard is the
    // invariant, not the UI (the Console's closeTab guard, ported).
    if (t?.surfaceId === 'home') return
    const def = t ? surfaces.find((s) => s.id === t.surfaceId) ?? surfaceById(t.surfaceId) : undefined
    if (def?.locked) return // nav.<id>.locked — locked surfaces NEVER close
    // Pinned tabs can't close (console parity) — but the pin is the EFFECTIVE
    // pin: a stored pin is void once the surface disallows pinning (the
    // stale-pin trap — pin hidden + pinned guard would be unclosable).
    if (t?.pinned && def?.allowPinning !== false) return
    // nav.<id>.allowClose === false: a tab this surface AUTO-LOADED at login
    // can't be dismissed; learner-opened tabs of the same surface still close.
    if (def?.allowClose === false && t?.autoLoaded) return
    setTabs((prev) => {
      const next = prev.filter((x) => x.id !== id)
      setActiveId((cur) => (cur === id ? (next[next.length - 1]?.id ?? null) : cur))
      return next
    })
  }

  /** Pin/unpin a tab (console parity): pinned tabs can't be closed and ride
   *  into the restored layout. nav.<id>.allowPinning === false removes the
   *  affordance — the strip hides the button; this guard is the invariant. */
  function togglePinTab(id: string) {
    const t = tabs.find((x) => x.id === id)
    const def = t ? surfaces.find((s) => s.id === t.surfaceId) ?? surfaceById(t.surfaceId) : undefined
    if (def?.allowPinning === false) return
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, pinned: !t.pinned } : t)))
  }

  /** Drag-to-reorder (the Console's moveTabs, ported): the dragged tab
   *  inserts before the drop target. Home is never draggable and never a
   *  drop target — it anchors the strip on the left. A folded group chip is
   *  a drop target for its first member. */
  function moveTabs(dragId: string, beforeId: string) {
    setTabs((prev) => {
      if (dragId === beforeId) return prev
      const arr = [...prev]
      const from = arr.findIndex((t) => t.id === dragId)
      if (from < 0 || arr[from].surfaceId === 'home') return prev
      const [moved] = arr.splice(from, 1)
      const to = arr.findIndex((t) => t.id === beforeId)
      if (to < 0 || arr[to].surfaceId === 'home') return prev
      arr.splice(to, 0, moved)
      return arr
    })
  }

  if (cfg === undefined) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  // Forced fullscreen (nav.fullscreen — CONSOLE-WIDE, Douglas 2026-10-03: one
  // flag for the whole LV, NOT a surface-by-surface selection): the chat
  // column is hidden completely, the maximize toggle is removed (the learner
  // can't leave fullscreen), and the mobile assistant never floats in.
  const forcedFs = cfg?.shell?.fullscreen === true
  const showChatColumn = chatAvailable === true && !maximized && !resizer.isMobile && !forcedFs
  const learnerName = me?.name || session.claims.name || ''
  const learnerEmail = me?.email || session.claims.email || ''
  const homeActive = tabs.find((t) => t.id === activeId)?.surfaceId === 'home'

  return (
    <LvConfigContext.Provider
      value={{ config: cfg, audio: { enabled: audioEnabled(cfg) }, nightModeAllowed: nightModeAllowed(cfg) }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* The masthead spans the full width; the split lives beneath it. */}
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.default' }}>
        <LvHeader
          cfg={cfg}
          homeLabel={homeTabLabel(cfg)}
          homeActive={homeActive}
          homeLocked={false}
          onHome={() => openHome()}
          explore={{
            homeNav: homeNav.items,
            currentFile: homeNav.currentFile,
            surfaces,
            menus,
            locked: false,
            onHomeNav: followHomeNav,
            onSurface: (id, label, params) => openSurface(id, label, params ? { params } : undefined),
            onLocked: () => undefined,
          }}
          account={{
            kind: 'learner',
            name: learnerName,
            email: learnerEmail,
            onSignOut: signOut,
          }}
          inbox={showInboxIcon ? { unread, onOpen: () => openSurface('inbox') } : undefined}
          appearance={{ allowed: appearanceAllowed, mode, onChoose: chooseAppearanceMode }}
        />
        {/* The split as two rounded CARDS on the app's ground (Douglas,
            2026-08-28) — not the console's hard-edged columns. Root padding
            gives the cards their margin; the resize handle lives in the gap. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden', p: 1 }}>
          {/* LEFT: the assistant column (console: chat pane). 0px while the
              probe is in flight or the deployment has no chat configured;
              maximized collapses it; mobile never splits. */}
          <Box
            sx={{
              width: showChatColumn ? `calc(100% - ${resizer.width}px - ${HANDLE_WIDTH}px)` : 0,
              flexShrink: 0,
              transition: resizer.dragging ? 'none' : SLIDE,
              overflow: 'hidden',
              borderRadius: 2,
              border: showChatColumn ? '1px solid' : 0,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: showChatColumn ? '0 4px 18px -12px rgba(0,0,0,0.35)' : 'none',
            }}
          >
            {showChatColumn && (
              <LvChat
                learnerName={learnerName}
                learnerEmail={learnerEmail}
                onOpenSurface={openSurface}
                onShowInSurface={showInSurface}
                onUnavailable={() => setChatAvailable(false)}
              />
            )}
          </Box>
          {showChatColumn && (
            <ResizeHandle
              onPointerDown={resizer.onHandlePointerDown}
              onKeyDown={resizer.onHandleKeyDown}
              wasDrag={resizer.wasDrag}
              dragging={resizer.dragging}
              valueNow={resizer.width}
              valueMin={resizer.min}
              valueMax={resizer.max}
              presets={resizer.presets}
              onPreset={resizer.setSplit}
            />
          )}
          {/* RIGHT: the surface column — strip bar + tab content. */}
          <Box
            sx={{
              width: resizer.isMobile || maximized || forcedFs || chatAvailable !== true ? '100%' : resizer.width,
              flexShrink: 0,
              flexGrow: chatAvailable !== true || maximized || forcedFs || resizer.isMobile ? 1 : 0,
              minWidth: 0,
              display: 'flex', flexDirection: 'column',
              transition: resizer.dragging ? 'none' : SLIDE,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: '0 4px 18px -12px rgba(0,0,0,0.35)',
            }}
          >
            {/* The strip bar — pinned to the SAME height as the assistant's
                bar so the two columns' divider lines land on one rule.
                Layout: tabs left; the masthead above owns brand/account/
                appearance AND surface launching (Explore carries the menuOp
                flyouts), so the only controls here are the optional maximize
                toggle and inbox badge. */}
            <Stack
              direction="row" alignItems="center"
              sx={{ borderBottom: 1, borderColor: 'divider', px: 0.5, bgcolor: 'background.paper', height: PANEL_BAR_HEIGHT, boxSizing: 'border-box', flexShrink: 0 }}
            >
              <Stack direction="row" alignItems="center" sx={{ overflowX: 'auto', flex: 1 }}>
                {buildStripItems(tabs).map((item) => {
                  if (item.kind === 'group') {
                    const groupDef = surfaces.find((s) => s.id === item.surfaceId) ?? surfaceById(item.surfaceId)
                    const groupLocked = groupDef?.locked === true
                    return (
                      <GroupChip
                        key={item.surfaceId}
                        surfaceId={item.surfaceId}
                        tabs={item.tabs}
                        activeId={activeId}
                        onSelect={setActiveId}
                        onClose={closeTab}
                        iconOf={iconOf}
                        flipOf={flipStateFor}
                        onFlip={flipTabFace}
                        onPin={togglePinTab}
                        locked={groupLocked}
                        allowPinning={groupDef?.allowPinning !== false}
                        allowClose={groupDef?.allowClose !== false}
                        onDropTab={moveTabs}
                      />
                    )
                  }
                  // Two-sided surfaces: the chip tells the face — the glyph
                  // swaps to the face's own, a face-coloured underline marks
                  // it, and the flip button turns the card over in place.
                  const flip = flipStateFor(item.tab)
                  const face = flip ? faceOf(item.tab) : undefined
                  const chipDef = surfaces.find((s) => s.id === item.tab.surfaceId) ?? surfaceById(item.tab.surfaceId)
                  const locked = chipDef?.locked === true
                  // The effective pin: a stored pin is void once the surface
                  // disallows pinning (the stale-pin trap).
                  const pinAllowed = chipDef?.allowPinning !== false
                  const effPinned = item.tab.pinned === true && pinAllowed
                  // nav.<id>.allowClose === false guards only AUTO-LOADED tabs.
                  const closeGuarded = chipDef?.allowClose === false && item.tab.autoLoaded === true
                  return (
                    <Stack
                      key={item.tab.id} direction="row" alignItems="center" spacing={0.5}
                      draggable={item.tab.surfaceId !== 'home'}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/lv-tab', item.tab.id)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragOver={(e) => {
                        if (item.tab.surfaceId !== 'home') { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }
                      }}
                      onDrop={(e) => {
                        const id = e.dataTransfer.getData('text/lv-tab')
                        if (id) { e.preventDefault(); moveTabs(id, item.tab.id) }
                      }}
                      onClick={() => setActiveId(item.tab.id)}
                      sx={{
                        cursor: 'pointer', px: 1.25, py: 0.75, borderRadius: 1.5, m: 0.5,
                        bgcolor: item.tab.id === activeId ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                        whiteSpace: 'nowrap',
                        ...(flip && face
                          ? { borderBottom: '2px solid', borderBottomColor: FACE_COLOR[face] ?? 'primary.main', borderRadius: '12px 12px 6px 6px' }
                          : {}),
                      }}
                    >
                      <SurfaceGlyph icon={(face && FACE_GLYPH[face]) || iconOf(item.tab.surfaceId)} />
                      <TabLabel label={item.tab.label} bold={item.tab.id === activeId} />
                      {flip && (
                        <Tooltip title={`Flip to ${flip.next.label} view`}>
                          <IconButton
                            size="small" aria-label={`Flip to ${flip.next.label} view`}
                            onClick={(e) => { e.stopPropagation(); flipTabFace(item.tab.id) }}
                            sx={{ p: 0.25 }}
                          >
                            <FlipIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Locked surfaces show the lock, never controls; pinned
                          tabs show the pin, never an X (console parity);
                          Home carries nothing at all. */}
                      {item.tab.surfaceId !== 'home' && (locked ? (
                        <Tooltip title="Locked by your organization — this tab can't be closed">
                          <LockIcon sx={{ fontSize: 12, color: 'text.disabled', ml: 0.25 }} />
                        </Tooltip>
                      ) : (
                        <>
                          {pinAllowed && (
                            <Tooltip title={effPinned ? 'Unpin the tab' : 'Pin the tab — pinned tabs can’t be closed'}>
                              <IconButton
                                size="small" aria-label={effPinned ? 'Unpin the tab' : 'Pin the tab'}
                                onClick={(e) => { e.stopPropagation(); togglePinTab(item.tab.id) }}
                                sx={{ p: 0.25 }}
                              >
                                <PushPinIcon sx={{ fontSize: 13, color: effPinned ? 'primary.main' : 'text.disabled', transform: effPinned ? 'none' : 'rotate(45deg)' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!effPinned && (closeGuarded ? (
                            <Tooltip title="Set to open automatically by your organization — this tab can't be closed">
                              <LockIcon sx={{ fontSize: 12, color: 'text.disabled', ml: 0.25 }} />
                            </Tooltip>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); closeTab(item.tab.id) }}
                              sx={{ p: 0.25 }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          ))}
                        </>
                      ))}
                    </Stack>
                  )
                })}
              </Stack>
              {chatAvailable === true && !resizer.isMobile && !forcedFs && (
                <Tooltip title={maximized ? 'Show the assistant (Esc)' : 'Fill the screen with surfaces'}>
                  <IconButton size="small" onClick={() => setMaximized((m) => !m)} sx={{ mx: 0.5, flexShrink: 0 }}>
                    {maximized ? <FullscreenExitIcon sx={{ fontSize: 16 }} /> : <FullscreenIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
              {tabs.map((t) => {
                // Two-sided surfaces: the Box keys on the face, so a flip
                // REMOUNTS the pane (a clean boot into the other side) and
                // the remount plays the card-flip. The fade is the
                // tab-switch transition — fired only on activation, never
                // the flip's job.
                const face = faceOf(t)
                const faceChanged = face !== undefined && lastFaceRef.current[t.id] !== face
                const becameActive = t.id === activeId && lastActiveRef.current !== t.id
                const anim = reduceMotion ? {} : faceChanged ? FLIP_SX : becameActive ? FADE_SX : {}
                return (
                  <Box
                    key={face ? `${t.id}:${face}` : t.id}
                    sx={{
                      position: 'absolute', inset: 0, display: t.id === activeId ? 'block' : 'none',
                      ...anim,
                    }}
                  >
                    {t.surfaceId === 'home' ? (
                      <LvHomeCanvas
                        offeredSurfaces={offeredIds}
                        onOpenSurface={(id) => openSurface(id)}
                        onNavChange={onHomeNavChange}
                        navFollowRef={homeFollowRef}
                      />
                    ) : (
                      <SurfacePane surfaceId={t.surfaceId} target={{ params: t.params, tabId: t.id, label: t.label }} />
                    )}
                  </Box>
                )
              })}
            </Box>
          </Box>
        </Box>
        </Box>

        {/* MOBILE: no split — the cap button floats the assistant in as a
            full-screen overlay (console mobile rule, inverted for LV: surfaces
            are the base here, the assistant visits on top). */}
        {chatAvailable === true && resizer.isMobile && !mobileChatOpen && !forcedFs && (
          <Fab
            color="primary" size="medium" aria-label="Open the assistant"
            onClick={() => setMobileChatOpen(true)}
            sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: (t) => t.zIndex.drawer }}
          >
            <AutoAwesomeIcon />
          </Fab>
        )}
        {chatAvailable === true && resizer.isMobile && mobileChatOpen && !forcedFs && (
          <Box
            sx={{
              position: 'fixed', inset: 0, zIndex: (t) => t.zIndex.modal,
              bgcolor: 'background.default', display: 'flex', flexDirection: 'column',
            }}
          >
            <LvChat
              learnerName={learnerName}
              learnerEmail={learnerEmail}
              onOpenSurface={openSurface}
              onShowInSurface={showInSurface}
              onUnavailable={() => { setChatAvailable(false); setMobileChatOpen(false) }}
              onClose={() => setMobileChatOpen(false)}
            />
          </Box>
        )}
      </ThemeProvider>
    </LvConfigContext.Provider>
  )
}
